/* tslint:disable:max-line-length */
import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {MmDbService} from '../../../mm-db/mm-db-service';
import {MmProjectDbCollections} from '../../../mm-db/mm-project-db-schema';
import {FaultPatch} from './fault-patch';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {FaultOperationPathEnum} from './fault-operation-path.enum';
import {MmPatchOperation} from '@docma-mm-core/patch/mm-patch-operation';
import {Guid} from 'guid-typescript';
import {Fault, ViewFault} from '@docma-mm-core/fault/fault';
import {MmSyncableModel} from '@docma-mm-core/model/mm-syncable.model';
import {groupBy, sortByDate} from '../../utils/collection-helpers';
import {BrowserDbService} from '../../../mm-db/browser-db.service';
import {MmProjectDbSchema} from '../../../mm-db/mm-project-db-schema';

/** Operation type mapping for change states */
const OPERATION_MAP: Record<ChangeStatusEnum, 'add' | 'remove' | 'replace'> = {
  [ChangeStatusEnum.New]: 'add',
  [ChangeStatusEnum.Deleted]: 'remove',
  [ChangeStatusEnum.Changed]: 'replace',
  [ChangeStatusEnum.NotChanged]: null
};

@Injectable()
export class FaultPatchService implements OnDestroy {

  /** DB connection cache for improved performance */
  private dbCache: Map<string, { db: BrowserDbService<MmProjectDbSchema>, lastAccess: number }> = new Map();
  private readonly DB_CACHE_TTL = 30000; // 30 seconds

  constructor(private readonly mmDbService: MmDbService, private ngZone: NgZone) {
  }

  ngOnDestroy(): void {
    this.dbCache.clear();
  }

  // ============================================================================
  // DB CONNECTION CACHING
  // ============================================================================

  /**
   * Gets a cached DB connection or creates a new one.
   * Improves performance by avoiding repeated connection setup.
   */
  private async getCachedDb(mydocmaUid: string): Promise<BrowserDbService<MmProjectDbSchema>> {
    const now = Date.now();
    const cached = this.dbCache.get(mydocmaUid);

    if (cached && (now - cached.lastAccess) < this.DB_CACHE_TTL) {
      cached.lastAccess = now;
      return cached.db;
    }

    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    this.dbCache.set(mydocmaUid, { db, lastAccess: now });
    this.cleanupDbCache();

    return db;
  }

  /** Cleans up expired DB cache entries */
  private cleanupDbCache(): void {
    const now = Date.now();
    for (const [key, value] of this.dbCache.entries()) {
      if (now - value.lastAccess > this.DB_CACHE_TTL) {
        this.dbCache.delete(key);
      }
    }
  }

  // ============================================================================
  // SAVE OPERATIONS
  // ============================================================================

  /**
   * Saves fault patches sequentially.
   * Use saveAll() for better performance when saving multiple patches.
   */
  public async save(mydocmaUid: string, faultPatches: FaultPatch[]): Promise<FaultPatch[]> {
    if (!faultPatches || faultPatches.length === 0) {
      return [];
    }

    return this.ngZone.runOutsideAngular(async () => {
      const db = await this.getCachedDb(mydocmaUid);

      // Use putAll for batch insert - more efficient than sequential
      await db.putAll(MmProjectDbCollections.fault_patches as never, faultPatches);
      return faultPatches;
    });
  }

  /**
   * Saves all fault patches in a single batch operation.
   * Most efficient method for saving multiple patches.
   */
  public async saveAll(mydocmaUid: string, faultPatches: FaultPatch[]): Promise<FaultPatch[]> {
    if (!faultPatches || faultPatches.length === 0) {
      return [];
    }

    return this.ngZone.runOutsideAngular(async () => {
      const db = await this.getCachedDb(mydocmaUid);
      await db.putAll(MmProjectDbCollections.fault_patches as never, faultPatches);
      return faultPatches;
    });
  }

  /**
   * Moves patches to backup (for error recovery).
   */
  public async moveToBackupAll(mydocmaUid: string, faultPatches: FaultPatch[]): Promise<FaultPatch[]> {
    if (!faultPatches || faultPatches.length === 0) {
      return [];
    }

    const db = await this.getCachedDb(mydocmaUid);
    const allPatches = await db.getAll(MmProjectDbCollections.fault_patches as never) as FaultPatch[];
    const patchesDict = this.groupByOperationId(allPatches);

    const backupPatches = faultPatches
      .filter(p => patchesDict[p.operation?.id]?.[0])
      .map(p => patchesDict[p.operation.id][0]);

    // TODO: Implement actual backup logic if needed
    return faultPatches;
  }

  // ============================================================================
  // DELETE OPERATIONS
  // ============================================================================

  /**
   * Removes patches by their IDs in a single transaction.
   */
  public async remove(mydocmaUid: string, ids: string[]): Promise<void> {
    if (!ids || ids.length === 0) {
      return;
    }

    return this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.getCachedDb(mydocmaUid);
      const idb = await dbService.db;
      const tx = idb.transaction(MmProjectDbCollections.fault_patches as never, 'readwrite');

      try {
        // Delete all in parallel within the transaction
        await Promise.all(ids.map(id => tx.store.delete(id)));
        await tx.done;
      } catch (err) {
        tx.abort();
        throw err;
      }
    });
  }

  /**
   * Removes all patches for given fault UIDs.
   */
  public async removeByFaultUids(mydocmaUid: string, fUids: string[]): Promise<void> {
    if (!fUids || fUids.length === 0) {
      return;
    }

    return this.ngZone.runOutsideAngular(async () => {
      const dbService = await this.getCachedDb(mydocmaUid);
      const idb = await dbService.db;
      const tx = idb.transaction(MmProjectDbCollections.fault_patches as never, 'readwrite');
      const store = tx.store;
      const index = store.index('fUid');

      try {
        // Collect all keys first, then delete in parallel
        const keysToDelete: IDBValidKey[] = [];
        for (const fUid of fUids) {
          const keys = await index.getAllKeys(fUid);
          keysToDelete.push(...keys);
        }

        await Promise.all(keysToDelete.map(key => store.delete(key)));
        await tx.done;
      } catch (err) {
        tx.abort();
        throw err;
      }
    });
  }

  // ============================================================================
  // READ OPERATIONS
  // ============================================================================

  /**
   * Gets patches by fault UID, sorted by date.
   */
  public async getByFaultUid(mydocmaUid: string, fUid: string): Promise<FaultPatch[]> {
    const dbService = await this.getCachedDb(mydocmaUid);
    const db = await dbService.db;
    const patches = await db.getAllFromIndex(MmProjectDbCollections.fault_patches as never, 'fUid', fUid) as FaultPatch[];
    return this.sortPatchesByDate(patches);
  }

  /**
   * Gets patches by group UID, sorted by date.
   */
  public async getByGroupUid(mydocmaUid: string, gUid: string): Promise<FaultPatch[]> {
    const dbService = await this.getCachedDb(mydocmaUid);
    const db = await dbService.db;
    const patches = await db.getAllFromIndex(MmProjectDbCollections.fault_patches as never, 'gUid', gUid) as FaultPatch[];
    return this.sortPatchesByDate(patches);
  }

  /**
   * Gets all patch keys.
   */
  public async getAllKeys(mydocmaUid: string): Promise<IDBValidKey[]> {
    const dbService = await this.getCachedDb(mydocmaUid);
    const db = await dbService.db;
    return db.getAllKeys(MmProjectDbCollections.fault_patches as never);
  }

  /**
   * Gets all patches.
   */
  public async getAll(mydocmaUid: string): Promise<FaultPatch[]> {
    const dbService = await this.getCachedDb(mydocmaUid);
    const db = await dbService.db;
    return db.getAll(MmProjectDbCollections.fault_patches as never);
  }

  // ============================================================================
  // OPERATION GENERATION
  // ============================================================================

  /**
   * Generates a patch operation from a change state.
   * Returns null for unchanged states.
   */
  public generateOperationFromChangeState(path: FaultOperationPathEnum, changeState: ChangeState<any>): MmPatchOperation | null {
    if (!changeState || changeState.status === ChangeStatusEnum.NotChanged) {
      return null;
    }

    const op = OPERATION_MAP[changeState.status];
    if (!op) {
      return null;
    }

    return {
      id: Guid.create().toString(),
      op,
      path,
      value: changeState.data
    };
  }

  // ============================================================================
  // GROUPING METHODS (using generic helper)
  // ============================================================================

  public groupByOuid(patches: FaultPatch[]): Record<string, FaultPatch[]> {
    return groupBy(patches, p => p.oUid, sortByDate);
  }

  public groupByOperationId(patches: FaultPatch[]): Record<string, FaultPatch[]> {
    return groupBy(patches, p => p.operation?.id, sortByDate);
  }

  public groupByPuid(patches: FaultPatch[]): Record<string, FaultPatch[]> {
    return groupBy(patches, p => p.pUid, sortByDate);
  }

  public groupByFuid(patches: FaultPatch[]): Record<string, FaultPatch[]> {
    return groupBy(patches, p => p.fUid, sortByDate);
  }

  public groupByGuid(patches: FaultPatch[]): Record<string, FaultPatch[]> {
    return groupBy(patches, p => p.gUid, sortByDate);
  }

  public groupByAuid(patches: FaultPatch[]): Record<string, FaultPatch[]> {
    return groupBy(patches, p => p.aUid, sortByDate);
  }

  // ============================================================================
  // CHANGE STATE GENERATION
  // ============================================================================

  /**
   * Generates a change state from patches.
   * Combines multiple patches into a single change state.
   */
  public generateChangeStateFromPatches(patches: FaultPatch[]): ChangeState<any> {
    if (!patches || patches.length === 0) {
      return new ChangeState({ status: ChangeStatusEnum.NotChanged, data: {} });
    }

    const sortedPatches = [...patches];
    let data: Record<string, any> = {};
    let status = ChangeStatusEnum.NotChanged;
    let changeDate: Date;

    const firstPatch = sortedPatches[0];

    // Determine initial state from first patch
    switch (firstPatch.operation.op) {
      case 'add':
        status = ChangeStatusEnum.New;
        changeDate = firstPatch.date;
        data = { ...firstPatch.operation.value };
        sortedPatches.shift();
        break;
      case 'remove':
        status = ChangeStatusEnum.Deleted;
        changeDate = patches[patches.length - 1].date;
        data = { ...firstPatch.operation.value };
        break;
      default:
        status = ChangeStatusEnum.Changed;
        changeDate = patches[patches.length - 1].date;
    }

    // Apply subsequent patches
    for (const patch of sortedPatches) {
      if (patch.operation.op === 'add' || patch.operation.op === 'replace') {
        Object.assign(data, patch.operation.value);
      } else if (patch.operation.op === 'remove') {
        status = ChangeStatusEnum.Deleted;
      }
    }

    return new ChangeState({ status, date: changeDate, data });
  }

  // ============================================================================
  // SYNC STATE LOADING
  // ============================================================================

  /**
   * Loads sync states for faults from patches.
   * Efficiently processes all faults and their nested entities.
   */
  public async loadSyncStates(allPatches: FaultPatch[], faults: ViewFault[] | Fault[]): Promise<Fault[]> {
    if (!faults || faults.length === 0) {
      return faults as Fault[];
    }

    // Pre-index patches by fUid and gUid for faster lookups
    const patchesByFUid = this.groupByFuid(allPatches);
    const patchesByGUid = this.groupByGuid(allPatches);

    for (const fault of faults) {
      // Get patches for this fault
      const faultPatches = patchesByFUid[fault.uid] || [];

      // Get group UIDs from fault patches and collect group patches
      const gUids = new Set(faultPatches.filter(p => p.gUid).map(p => p.gUid));
      const groupPatches = Array.from(gUids).flatMap(gUid => patchesByGUid[gUid] || []);

      // Combine and deduplicate patches
      const allFaultPatches = this.deduplicatePatches([...faultPatches, ...groupPatches]);

      // Process fault
      this.loadEntitySyncState(fault, allFaultPatches, [FaultOperationPathEnum.fault, FaultOperationPathEnum.faults]);
      fault.attributeId = fault.data?.Attribute?.Id;

      // Process fault attachments
      this.loadAttachmentsSyncStates(fault.attachments, allFaultPatches, false);

      // Process activities
      for (const activity of fault.activities) {
        this.loadEntitySyncState(activity, allFaultPatches, [FaultOperationPathEnum.activity, FaultOperationPathEnum.groupActivity]);
        this.loadAttachmentsSyncStates(activity.attachments, allFaultPatches, true);
      }
    }

    return faults as Fault[];
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /** Sorts patches by date ascending */
  private sortPatchesByDate(patches: FaultPatch[]): FaultPatch[] {
    return patches.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  /** Deduplicates patches by operation ID */
  private deduplicatePatches(patches: FaultPatch[]): FaultPatch[] {
    const seen = new Set<string>();
    return patches.filter(p => {
      const key = p.operation?.id;
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /** Loads sync state for a single entity */
  private loadEntitySyncState(entity: MmSyncableModel<any>, patches: FaultPatch[], paths: FaultOperationPathEnum[]): void {
    const entityPatches = patches.filter(p =>
      paths.includes(p.operation.path as FaultOperationPathEnum) &&
      p.oUid === entity.uid
    );

    const syncState = { ...entity.syncState };
    const changeState = this.generateChangeStateFromPatches(entityPatches);
    syncState.changes = changeState.status !== ChangeStatusEnum.NotChanged ? [changeState] : [];
    entity.syncState = syncState;
  }

  /** Loads sync states for attachments and their scribbles */
  private loadAttachmentsSyncStates(attachments: any[], patches: FaultPatch[], isActivity: boolean): void {
    const attachmentPath = isActivity ? FaultOperationPathEnum.activityAttachment : FaultOperationPathEnum.attachment;
    const scribblePath = isActivity ? FaultOperationPathEnum.activityAttachmentScribbles : FaultOperationPathEnum.attachmentScribbles;

    for (const attachment of attachments) {
      // Load attachment sync state
      const attachmentPatches = patches.filter(p =>
        p.operation.path === attachmentPath && p.oUid === attachment.uid
      );
      this.applySyncState(attachment, attachmentPatches);

      // Load scribble sync states
      for (const scribble of (attachment.scribbles || [])) {
        const scribblePatches = patches.filter(p =>
          p.operation.path === scribblePath &&
          p.pUid === attachment.uid &&
          p.oUid === scribble.uid
        );
        this.applySyncState(scribble, scribblePatches);
      }
    }
  }

  /** Applies sync state to an entity */
  private applySyncState(entity: MmSyncableModel<any>, patches: FaultPatch[]): void {
    const syncState = { ...entity.syncState };
    const changeState = this.generateChangeStateFromPatches(patches);
    syncState.changes = changeState.status !== ChangeStatusEnum.NotChanged ? [changeState] : [];
    entity.syncState = syncState;
  }
}

// ============================================================================
// EXPORTED HELPER FUNCTIONS
// ============================================================================

/**
 * Extracts unique fault UIDs from patches.
 */
export function faultUidsOfPatches(allPatches: FaultPatch[]): string[] {
  if (!allPatches || allPatches.length === 0) {
    return [];
  }

  const uids = new Set<string>();
  for (const patch of allPatches) {
    if (patch.fUid) {
      uids.add(patch.fUid);
    }
  }
  return Array.from(uids);
}

/**
 * Groups patches by fault UID.
 * Use FaultPatchService.groupByFuid() for instance method.
 */
export function patchesByFUid(allPatches: FaultPatch[]): Record<string, FaultPatch[]> {
  return groupBy(allPatches ?? [], p => p.fUid, sortByDate);
}

/**
 * Groups patches by group UID.
 * Use FaultPatchService.groupByGuid() for instance method.
 */
export function patchesByGUid(allPatches: FaultPatch[]): Record<string, FaultPatch[]> {
  return groupBy(allPatches ?? [], p => p.gUid, sortByDate);
}
